import Check from "@mui/icons-material/Check";
import { Box, Typography } from "@mui/material";
import { WasteManifestSource } from "generated-graphql/graphql";
import { Fragment, useEffect, useState } from "react";

import { SOURCE_LABEL, SOURCE_ORDER } from "./manifestSourceDisplay";

interface SourcesDisagreeTooltipProps {
  /** Per-source display values for the field; missing/blank sources are omitted. */
  values: Partial<Record<WasteManifestSource, string | null | undefined>>;
}

/**
 * Tooltip body for the "Sources disagree on this value" warning: the message
 * plus a two-column Source / value table so the reader sees each source's
 * reported value aligned. Shared by the manifest-detail fields, the line-item
 * modal, and the line-item grid's Amount cell.
 *
 * Each value is a button: clicking it copies that source's value to the
 * clipboard (a reviewer often wants to paste the correct value into the field
 * they're fixing). Works because the wrapping MUI Tooltip is interactive by
 * default, so the pointer can move onto the value without dismissing it.
 */
export function SourcesDisagreeTooltip({ values }: SourcesDisagreeTooltipProps) {
  const [copiedSource, setCopiedSource] = useState<WasteManifestSource | null>(null);

  // Clear the "Copied" confirmation shortly after it shows. Cleaned up if the
  // tooltip unmounts or another value is copied before it elapses.
  useEffect(() => {
    if (copiedSource == null) return undefined;
    const timer = setTimeout(() => setCopiedSource(null), 1200);
    return () => clearTimeout(timer);
  }, [copiedSource]);

  const handleCopy = (source: WasteManifestSource, value: string) => {
    // clipboard is unavailable outside secure contexts (e.g. plain http); fail
    // quietly rather than throwing inside a tooltip.
    const clipboard = navigator.clipboard;
    if (!clipboard) return;
    void clipboard.writeText(value).then(
      () => setCopiedSource(source),
      () => {}
    );
  };

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
        Sources disagree on this value
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          columnGap: 1.5,
          rowGap: 0.25,
          alignItems: "baseline",
        }}
      >
        {SOURCE_ORDER.map((source) => {
          const value = values[source];
          if (value == null || value === "") return null;
          const copied = copiedSource === source;
          return (
            <Fragment key={source}>
              <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {SOURCE_LABEL[source]}
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={() => handleCopy(source, value)}
                aria-label={`Copy ${SOURCE_LABEL[source]} value`}
                title="Click to copy"
                sx={{
                  appearance: "none",
                  border: "none",
                  background: "none",
                  p: 0,
                  m: 0,
                  font: "inherit",
                  color: "inherit",
                  textAlign: "left",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: 0.5,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <Typography variant="caption" component="span">
                  {value}
                </Typography>
                {copied && (
                  <Typography
                    variant="caption"
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.25,
                      // White to stay legible on the dark tooltip background
                      // (success green was too dark to read there).
                      color: "common.white",
                    }}
                  >
                    <Check sx={{ fontSize: "0.9rem" }} />
                    Copied
                  </Typography>
                )}
              </Box>
            </Fragment>
          );
        })}
      </Box>
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 0.5, opacity: 0.7, fontStyle: "italic" }}
      >
        Click a value to copy
      </Typography>
    </Box>
  );
}
